<script setup lang="ts">
import {
  bemBuilder,
  debounce,
  phoneBeautifier,
  parseInputNumber,
  findCountryByInternationCode,
} from "@chatfood/core-utils";
import { computed, reactive, ref } from "vue";
import {
  AtomInput,
  AtomText,
  AtomInputPhoneNumber,
  SatFormControl,
} from "@chatfood/design-system";
import { useBusinessStore } from "~/stores/business";
import { useGuestStore } from "~/stores/guest";
import { useLocale } from "~/composables/use-locale";
import type { IDomainGuest } from "~/domain/guest";

const css = bemBuilder("guest-form");

const business = useBusinessStore();
const guest = useGuestStore();

const { t } = useLocale();

const formData = reactive<IDomainGuest>({
  name: guest.name,
  email: guest.email,
  phoneNumber: guest.phoneNumber,
  countryCode: guest.countryCode,
});

const debouncedUpdate = debounce(() => {
  const plainData = {
    name: formData.name,
    email: formData.email,
    phoneNumber: formData.phoneNumber,
    countryCode: formData.countryCode,
  };

  if (
    plainData.name === guest.name &&
    plainData.email === guest.email &&
    plainData.phoneNumber === guest.phoneNumber &&
    plainData.countryCode === guest.countryCode
  ) {
    return;
  }

  guest.update(plainData);
}, 1000);

const autofocusField = computed(() => {
  const currentName = formData.name;
  const currentEmail = formData.email;
  const currentPhoneNumber = formData.phoneNumber;
  const currentCountryCode = formData.countryCode;

  if (!currentName || currentName.length < 4) {
    return "name";
  }

  if (!currentEmail || !checkValidEmail(currentEmail)) {
    return "email";
  }

  if (
    !parseInputNumber(
      phoneBeautifier(currentPhoneNumber ?? "", currentCountryCode, "e164") ??
        "",
    ).valid
  ) {
    return "phoneNumber";
  }

  return undefined;
});

const checkValidEmail = (email: string) => {
  if (!email) return false;

  const regexEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regexEmail.test(email);
};

const errorMessageName = ref("");
const setName = (name: string) => {
  if (name === formData.name) return;

  errorMessageName.value = "";

  formData.name = name || undefined;

  if (!name) {
    errorMessageName.value = t("components.guest_form.error_required_name");
  }

  debouncedUpdate();
};

const errorMessageEmail = ref("");
const setEmail = (email: string) => {
  if (email === formData.email) return;

  errorMessageEmail.value = "";

  formData.email = email || undefined;

  if (!email) {
    errorMessageEmail.value = t("components.guest_form.error_required_email");
  } else if (!checkValidEmail(email)) {
    errorMessageEmail.value = t("components.guest_form.error_invalid_email");
  }

  debouncedUpdate();
};

type ISetPhoneNumberPayload = {
  number?: string;
  countryCode?: number;
  isValid: boolean;
};

const errorMessagePhoneNumber = ref("");
const setPhoneNumber = (payload: ISetPhoneNumberPayload) => {
  const finalPhoneNumber = payload.number
    ? payload.number.replace(/\D/g, "")
    : undefined;
  const finalCountryCode = payload.countryCode
    ? payload.countryCode.toString()
    : undefined;

  if (
    finalPhoneNumber === formData.phoneNumber &&
    finalCountryCode === formData.countryCode
  ) {
    return;
  }

  errorMessagePhoneNumber.value = "";

  formData.phoneNumber = finalPhoneNumber;
  formData.countryCode = finalCountryCode;

  if (!payload.number) {
    errorMessagePhoneNumber.value = t(
      "components.guest_form.error_required_phone_number",
    );
  } else if (!payload.isValid) {
    errorMessagePhoneNumber.value = t(
      "components.guest_form.error_invalid_phone_number",
    );
  }

  debouncedUpdate();
};

const guestCountryIso2 = computed(() =>
  formData.countryCode
    ? findCountryByInternationCode(formData.countryCode)?.iso2
    : business.country,
);
</script>

<template>
  <div :class="css('')">
    <div :class="css('title')">
      <AtomText type="subtitle">
        {{ t("components.guest_form.personal_title") }}
      </AtomText>
      <AtomText color="color-text-secondary" type="small">
        {{ t("components.guest_form.personal_info_required") }}
      </AtomText>
    </div>
    <SatFormControl :error-message="errorMessageName" data-test="name">
      <AtomInput
        :value="formData.name"
        :autofocus="autofocusField === 'name'"
        :has-error="Boolean(errorMessageName)"
        :placeholder="t('components.guest_form.placeholder_name')"
        @blur="setName"
      />
    </SatFormControl>
    <SatFormControl :error-message="errorMessageEmail" data-test="email">
      <AtomInput
        type="email"
        :value="formData.email"
        :autofocus="autofocusField === 'email'"
        :has-error="Boolean(errorMessageEmail)"
        :placeholder="t('components.guest_form.placeholder_email')"
        @blur="setEmail"
      />
    </SatFormControl>
    <SatFormControl
      :error-message="errorMessagePhoneNumber"
      data-test="phoneNumber"
    >
      <ClientOnly>
        <AtomInputPhoneNumber
          :country="guestCountryIso2"
          :number="formData.phoneNumber"
          :autofocus="autofocusField === 'phoneNumber'"
          :has-error="Boolean(errorMessagePhoneNumber)"
          @blur="setPhoneNumber"
        />
      </ClientOnly>
    </SatFormControl>
  </div>
</template>

<style lang="scss" scoped>
.guest-form {
  display: flex;
  gap: var(--size-sm);
  flex-direction: column;

  &__title {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
  }
}
</style>
